import React from "react";
import LoadingCircle from "../../../utils/LoadingCircle";
import Alerts from "../Alerts";
import DocumentUploadForm from "./DocumentUploadForm";
import Step9AuditReview from "../Step9AuditReview"; // Import the new component

const renderContent = ({
  step, // Represents the current step in the document processing workflow
  currentSection, // Information about the current section being worked on
  handleFileChange, // Function to handle file selection
  handleDocumentUpload, // Function to handle document upload
  uniqueId, // Unique identifier for the current document or session
  uploadedFiles, // Object containing the uploaded files
  missingFiles, // List of required files that are missing
  result, // Result object that contains error or success messages
  documentStepMemory, // Consolidated memory for all extracted data, storing intermediate step results
  setState, // Function to update the state of the component
  loadingLabel, // Label to be displayed when the system is processing
  systemName, // The name of the current system in use
  manageDocumentSteps, // Function to manage and progress document steps
  onCancel, // Function to handle cancellation action for Step9AuditReview
  onDataPointsUpdate, // call back function to update form data in InputFormAI
  formData,
}) => {
  switch (step.current) {
    case "upload":
      // Render the document upload form when in the "upload" step
      return (
        <DocumentUploadForm
          currentSection={currentSection} // Provide the current section information to the form
          handleFileChange={handleFileChange} // Pass the function to handle file changes
          handleDocumentUpload={handleDocumentUpload} // Pass the function to handle document upload
          uniqueId={uniqueId} // Pass the unique identifier
          uploadedFiles={uploadedFiles} // Pass the uploaded files to the form
          missingFiles={missingFiles} // Pass the list of missing files, if any
        />
      );
    case "processing":
      // Render the loading circle when in the "processing" step
      return <LoadingCircle label={loadingLabel || "Processing..."} />;
    case "uploadError":
      // Render an alert in case of an upload error
      return (
        <Alerts
          type="retry" // Alert type set to retry
          message={result.message} // Display the error message from the result object
          onConfirm={() => {
            // Update step.current to the value of step.previous and set step.previous to the current value
            setState((prevState) => ({
              ...prevState,
              step: {
                current: prevState.step.previous, // Retry the previous step
                previous: "uploadError", // Set the previous to the current error state or any custom value to indicate error context
              },
            }));

            // Now, `useEffect` in `UploadDocument` will detect the change in `step.current`
            // and trigger `manageDocumentSteps` for the retry automatically.
          }}
          onCancel={() => {
            // Go back to the upload step if the user cancels the retry
            setState((prevState) => ({
              ...prevState,
              result: null, // Clear the result object
              step: {
                previous: prevState.step.current, // Set previous to current before resetting
                current: "upload", // Reset to the upload step
              },
            }));
          }}
        />
      );
    case "auditReview": // Step 9: Render the audit review
      // Check if auditRiskProfile exists in documentStepMemory
      if (!documentStepMemory.auditRiskProfile) {
        // If auditRiskProfile is undefined, set step to "uploadError" or render a loading message
        setState((prevState) => ({
          ...prevState,
          step: {
            previous: prevState.step.current,
            current: "uploadError",
          },
          result: {
            type: "error",
            message: "Audit Risk Profile data is not available. Please retry.",
          },
        }));
        return null;
      }

      // Render the audit review component when in the "auditReview" step
      return (
        <Step9AuditReview
          documentStepMemory={documentStepMemory} // Pass the audit risk profile data directly
          systemName={systemName} // Pass the system name
          onCancel={onCancel} // Provide the onCancel function to handle the cancel action
          setStep={(newStep) =>
            // Function to update the step number to proceed to step 10 or another step
            setState((prevState) => ({
              ...prevState,
              step: {
                previous: prevState.step.current, // Set previous to the current step before proceeding
                current: newStep, // Update the current step in the state to proceed
              },
            }))
          }
          onDataPointsUpdate={onDataPointsUpdate}
          formData
        />
      );
    default:
      // Return null if the step is not recognized
      return null;
  }
};

export default renderContent;